<template>
  <div class="card-chat-history h-100">
    <!-- Actions -->
    <div class="d-flex justify-content-between">
      <b-button variant="outline-secondary" class="mr-1" @click="$emit('new-chat')"><feather-icon icon="EditIcon" /></b-button>
      <b-button variant="outline-secondary" class="w-100" @click="$emit('redirect-explore-assistants')">
        <feather-icon icon="StarIcon" class="mr-1" /> {{ $t('ia.assistants.exploreAssistants') }}
      </b-button>
      <b-button variant="outline-secondary" class="ml-1" @click="$emit('toggle-layout')"><feather-icon icon="LayoutIcon" /></b-button>
    </div>

    <!-- Select persona -->

    <div class="d-flex justify-content-between align-items-center mt-2 mb-05">
      <p class="mb-0 f-12 text-uppercase">{{ $t('ia.assistants.selectedPersona') }}</p>
      <span class="cursor-pointer avenir-medium text-uppercase" @click="$emit('open-persona-switcher')" v-if="existParamsUuid">{{ $t('ia.assistants.change') }}</span>
    </div>
    <div class="select-persona" v-if="assistant">
      <div class="d-flex align-items-center mb-05">
        <b-avatar :src="assistant.image ? getAssetUrl(assistant.image) : assistant.image_url"></b-avatar>
        <div class="d-flex flex-column ml-05">
          <strong>{{ assistant.name }}</strong>
          <p class="avenir-light m-0">{{ $t('ia.type_assistant').find((i) => i.value === assistant.type_assistant).text }}</p>
        </div>
      </div>
    </div>
    <div class="select-persona empty-state" v-else>
      <div class="d-flex align-items-center text-center">
        <feather-icon icon="UserIcon" size="32" class="text-muted" />
        <div class="d-flex flex-column ml-1">
          <p class="avenir-medium m-0 mb-05">{{ $t('ia.assistants.noAssistantSelected') }}</p>
          <p class="avenir-light m-0 text-muted small">{{ $t('ia.assistants.selectAssistantToStart') }}</p>
        </div>
      </div>
    </div>

    <div class="border-top mt-2 pt-2 overflow-scroll-y">
      <!-- Loading Skeletons -->
      <div v-if="loading" class="skeleton-container">
        <div v-for="i in 10" :key="`skeleton-${i}`" class="chat-list-skeleton mb-1">
          <div class="d-flex align-items-center">
            <b-skeleton type="avatar" animation="wave" class="flex-shrink-0"></b-skeleton>
            <b-skeleton animation="wave" width="80%" class="ml-1"></b-skeleton>
          </div>
        </div>
      </div>

      <!-- Actual conversations -->
      <div v-else>
        <template v-for="group in conversationGroups">
          <div v-if="group.conversations.length > 0" :key="group.key" class="mb-2">
            <h6 class="group-header">{{ group.label }}</h6>
            <div 
              v-for="conversation in group.conversations"
              :key="conversation.uuid"
              class="chat-list"
              :class="{ 'active-chat-list': isSelectedConversation(conversation) }"
              @click="$emit('select', conversation, getConversationIndex(conversation)); $emit('not-edit-title')"
            >
              <div class="d-flex w-100">
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex flex-grow-1 min-width-0 align-items-center">
                      <span
                        class="text-title-span"
                        :title="isAssistantSection ? conversation.prompt : conversation.title"
                      >
                        {{ isAssistantSection ? conversation.prompt : conversation.title }}
                      </span>
                    </div>
                    <div v-if="isSelectedConversation(conversation) && !isEditing && !isAssistantSection" class="d-flex flex-shrink-0 ml-1">
                      <feather-icon icon="EditIcon" class="icon-chat-list" @click.stop="$emit('edit', getConversationIndex(conversation))" />
                    </div>
                  </div>
                </div>
                <div v-if="isSelectedConversation(conversation)" class="w-auto">
                  <div v-if="isEditing && !isAssistantSection" class="d-flex align-items-center">
                    <b-form-input ref="form-input-title" @click.stop :value="titleValue" @input="$emit('title-input', $event)" @keyup.enter="$emit('confirm', conversation, getConversationIndex(conversation))" />
                    <feather-icon icon="CheckIcon" class="icon-chat-list mr-1 ml-1" @click.stop="$emit('confirm', conversation, getConversationIndex(conversation))" />
                    <feather-icon icon="XIcon" class="icon-chat-list" @click.stop="$emit('cancel', getConversationIndex(conversation))" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BFormInput, BAvatar, BSkeleton } from 'bootstrap-vue';
import { getAssetUrl } from '@/libs/utils/urls';

export default {
  name: 'ChatHistoryPanel',
  components: { BButton, BFormInput, BAvatar, BSkeleton },
  props: {
    conversations: { type: Array, default: () => [] },
    selectedIndex: { type: Number, default: null },
    conversationSelected: { type: Object, default: null },
    isEditing: { type: Boolean, default: false },
    titleValue: { type: String, default: '' },
    assistant: { type: Object, default: null},
    loading: { type: Boolean, default: false }
  },
  computed: {
    isAssistantSection() {
      return this.$route.params.section === 'assistants';
    },
    existParamsUuid() {
      return !this.$route.params.uuid;
    },
    groupedConversations() {
      const groups = {
        today: [],
        yesterday: [],
        last7Days: [],
        last30Days: [],
        months: {},
        years: {}
      };

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);

      this.conversations.forEach(conversation => {
        const date = new Date(conversation.created_at || conversation.updated_at || conversation.date);
        const conversationDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        if (conversationDate.getTime() === today.getTime()) {
          groups.today.push(conversation);
        } else if (conversationDate.getTime() === yesterday.getTime()) {
          groups.yesterday.push(conversation);
        } else if (conversationDate >= sevenDaysAgo && conversationDate < yesterday) {
          groups.last7Days.push(conversation);
        } else if (conversationDate >= thirtyDaysAgo && conversationDate < sevenDaysAgo) {
          groups.last30Days.push(conversation);
        } else {
          const year = date.getFullYear();
          const month = date.toLocaleString('en-US', { month: 'long' });
          
          if (year === now.getFullYear()) {
            if (!groups.months[month]) {
              groups.months[month] = [];
            }
            groups.months[month].push(conversation);
          } else {
            if (!groups.years[year]) {
              groups.years[year] = [];
            }
            groups.years[year].push(conversation);
          }
        }
      });

      return groups;
    },
    conversationGroups() {
      const grouped = this.groupedConversations;
      const result = [];

      // Add time-based groups
      result.push({ key: 'today', label: this.$t('ia.historyGroups.today'), conversations: grouped.today });
      result.push({ key: 'yesterday', label: this.$t('ia.historyGroups.yesterday'), conversations: grouped.yesterday });
      result.push({ key: 'last7Days', label: this.$t('ia.historyGroups.last7Days'), conversations: grouped.last7Days });
      result.push({ key: 'last30Days', label: this.$t('ia.historyGroups.last30Days'), conversations: grouped.last30Days });

      // Add months
      Object.keys(grouped.months).forEach(month => {
        const locale = this.$i18n?.locale || 'en';
        const dateForMonth = new Date(`${month} 1, 2000`);
        const localized = dateForMonth.toLocaleString(locale, { month: 'long' });
        result.push({
          key: `month-${month}`,
          label: localized.charAt(0).toUpperCase() + localized.slice(1),
          conversations: grouped.months[month]
        });
      });

      // Add years
      Object.keys(grouped.years).forEach(year => {
        result.push({
          key: `year-${year}`,
          label: year,
          conversations: grouped.years[year]
        });
      });

      return result;
    }
  },  
  data () {
    return {
      getAssetUrl
    }
  },
  methods: {
    getConversationIndex(conversation) {
      return this.conversations.findIndex(c => c.uuid === conversation.uuid);
    },
    isSelectedConversation(conversation) {
      return this.getConversationIndex(conversation) === this.selectedIndex;
    }
  }
}
</script>

<style scoped lang="scss">
.card-chat-history {
  .f-12 {
    font-size: 12px;
  }
  width: 100%;
  max-width: 400px;
  flex-shrink: 0;
  margin: 0;
  overflow-y: scroll;

  .select-persona {
    background-color: rgb(243, 243, 243);
    padding: 0.6em;
    border-radius: 5px;
    border: 0.4px solid #ccc;

    &.empty-state {
      
      .text-muted {
        color: #626666 !important;
      }
    }
  }
  
  .overflow-scroll-y {
    height: 92%;
    overflow-y: scroll;
  }

  .group-header {
    font-family: 'avenir-heavy';
    font-size: 12px;
    color: #6c757d;
    padding-left: 0.5em;
    letter-spacing: 0.5px;
    text-transform: capitalize;
  
  }
  
  .chat-list {
    padding: 8px;
    margin-bottom: .25em;
    border-radius: 0.5em;
    font-family: 'avenir-medium';
    display: flex;
    position: relative;

    &:hover {
      background-color: #dee2e6af;
      cursor: pointer;
    }
    
    .icon-chat-list {
      width: 20px;
      height: 20px;
      display: block;
    }
    
    .text-title-span {
      display: block;
      max-width: 100%;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 12px;
    }
  }
  
  .active-chat-list {
    background-color: #dee2e6 !important;
  }

  /* Utility: allow flex children to actually shrink so ellipsis can work */
  .min-width-0 {
    min-width: 0;
  }

  /* Skeleton loading styles */
  .chat-list-skeleton {
    padding: 1em;
    border-radius: 0.5em;
  }

  .skeleton-container {
    animation: fadeIn 0.3s ease-in;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
